import { useCallback, useRef } from 'react';
import {
  IconStatusCorrect,
  Stack,
  useToast,
} from '@adc/parallax-component-library';

import { useDepartmentDetails } from '~/api/query/departmentDetails';
import { useDepartmentUpdateMutation } from '~/api/query/departmentUpdate';
import {
  DepartmentForm,
  type DepartmentFormRef,
  type DepartmentFormValues,
} from '~/components/forms/department/departmentForm';
import { Modal } from '~/components/Modal';
import { useModal } from '~/features/modals/useModal';
import { DOMAINS } from '~/features/navigation/domains';
import { useDomainName } from '~/features/navigation/useDomainName';
import { buildTestId } from '~/utils/buildTestId';

type DepartmentDetailsFormModalProps = {
  testID: string;
  departmentId?: string;
};

export const DepartmentEditDetailsFormModal = ({
  testID,
  departmentId,
}: DepartmentDetailsFormModalProps) => {
  const { showToast } = useToast();
  const { closeModal } = useModal();
  const domain = useDomainName();
  const formRef = useRef<DepartmentFormRef>(null);

  const { data: department, isLoading } = useDepartmentDetails(departmentId);

  const { mutateAsync: updateDepartment } = useDepartmentUpdateMutation();

  const onFormSubmit = useCallback(
    async (values: DepartmentFormValues) => {
      await updateDepartment(values);

      closeModal('DepartmentEditDetailsForm');

      showToast({
        message: 'Update successful',
        'aria-label': 'Update successful',
        icon: <IconStatusCorrect accentColor="$support.positive.default" />,
      });
    },
    [closeModal, showToast, updateDepartment],
  );

  return (
    <Modal
      testID={testID}
      title={isLoading ? 'Loading' : 'Edit Department profile'}
      hasCloseButton
      size="m"
      primaryAction={{
        label: 'Save Changes',
        onPress: () => {
          void formRef.current?.submitForm();
        },
      }}
      secondaryAction={{
        label: 'Cancel',
        onPress: () => {
          closeModal('DepartmentEditDetailsForm');
        },
      }}
      onOpenChange={() => {
        closeModal('DepartmentEditDetailsForm');
      }}
    >
      <Stack paddingTop={domain !== DOMAINS.USER_ADMINISTRATION ? '$2' : '$0'}>
        <DepartmentForm
          ref={formRef}
          testID={buildTestId({
            testID,
            suffix: 'form',
          })}
          hiddenFields={['idnName', 'idnId']}
          disabledFields={['country']}
          onSubmit={onFormSubmit}
          defaultValues={
            department
              ? {
                  idnId: department.idn.id,
                  idnName: department.idn.name,
                  id: department.id,
                  careTeamId: department.careTeamId,
                  name: department.name,
                  phone: department.phone,
                  address: department.address.mainLine,
                  addressLine2: department.address.secondaryLine,
                  state: department.address.state,
                  city: department.address.city,
                  country: department.address.country,
                  zipCode: department.address.zipCode,
                }
              : undefined
          }
        />
      </Stack>
    </Modal>
  );
};
